import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { GraphQLClient, gql } from 'graphql-request';
import { Calendar, User, ArrowLeft, Clock, Twitter, Linkedin, Share2, Facebook } from 'lucide-react';

// Custom Reddit icon component
function RedditIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
    </svg>
  );
}

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Display from '@/components/typography/Display';
import { Container, HeroSection } from '@/components/ui/Section';
import Button from '@/components/ui/Button';
import ChecklistCTA from '@/components/ChecklistCTA';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath, withLanguagePrefix } from '@/lib/locale';
import Seo from '@/components/Seo';
import { JsonLd } from '@/components/JsonLd';
import { BASE_URL, getHreflangLinks } from '@/seo/meta';
import type { YoastSEOData } from '@/lib/wordpress-seo';

// WordPress GraphQL endpoint
const WP_GRAPHQL_URL = import.meta.env.VITE_WP_GRAPHQL_URL || 'https://palegreen-duck-273209.hostingersite.com/graphql';

type PostSEO = Partial<YoastSEOData> & {
  schema?: {
    raw?: string;
  };
};

interface Post {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  slug: string;
  date: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText: string;
    };
  };
  author?: {
    node: {
      name: string;
      avatarUrl?: string;
      avatar?: {
        url?: string;
      };
    };
  };
  tags?: {
    nodes: {
      name: string;
      slug: string;
    }[];
  };
  seo?: PostSEO;
}

interface PostDetailProps {
  postType?: 'post' | 'knowledge' | 'guides' | 'tools' | 'legislation' | 'organizations' | 'resources' | 'faqs';
}

// GraphQL query for single post by slug
const POST_BY_SLUG_QUERY = gql`
  query GetPostBySlug($slug: ID!) {
    post(id: $slug, idType: SLUG) {
      id
      title
      content
      excerpt
      slug
      date
      featuredImage {
        node {
          sourceUrl
          altText
        }
      }
      author {
        node {
          name
          avatar {
            url
          }
        }
      }
      categories {
        nodes {
          name
          slug
        }
      }
      tags {
        nodes {
          name
          slug
        }
      }
      seo {
        title
        metaDesc
        canonical
        opengraphTitle
        opengraphDescription
        opengraphImage {
          sourceUrl
        }
        twitterTitle
        twitterDescription
        twitterImage {
          sourceUrl
        }
        schema {
          raw
        }
      }
    }
  }
`;

// GraphQL queries for CPTs
const CPT_QUERIES: Record<string, string> = {
  knowledge: gql`
    query GetKnowledgeBySlug($slug: ID!) {
      knowledgeItem(id: $slug, idType: SLUG) {
        id
        title
        content
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        author {
          node {
            name
            avatarUrl
          }
        }
        seo {
          title
          metaDesc
          canonical
          opengraphTitle
          opengraphDescription
          opengraphImage {
            sourceUrl
          }
          twitterTitle
          twitterDescription
          twitterImage {
            sourceUrl
          }
          schema {
            raw
          }
        }
      }
    }
  `,
  guides: gql`
    query GetGuideBySlug($slug: ID!) {
      guide(id: $slug, idType: SLUG) {
        id
        title
        content
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        author {
          node {
            name
            avatarUrl
          }
        }
        seo {
          title
          metaDesc
          canonical
          opengraphTitle
          opengraphDescription
          opengraphImage {
            sourceUrl
          }
          twitterTitle
          twitterDescription
          twitterImage {
            sourceUrl
          }
          schema {
            raw
          }
        }
      }
    }
  `,
  tools: gql`
    query GetToolBySlug($slug: ID!) {
      tool(id: $slug, idType: SLUG) {
        id
        title
        content
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        author {
          node {
            name
            avatarUrl
          }
        }
        seo {
          title
          metaDesc
          canonical
          opengraphTitle
          opengraphDescription
          opengraphImage {
            sourceUrl
          }
          twitterTitle
          twitterDescription
          twitterImage {
            sourceUrl
          }
          schema {
            raw
          }
        }
      }
    }
  `,
  legislation: gql`
    query GetLegislationBySlug($slug: ID!) {
      legislationItem(id: $slug, idType: SLUG) {
        id
        title
        content
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        author {
          node {
            name
            avatarUrl
          }
        }
        seo {
          title
          metaDesc
          canonical
          opengraphTitle
          opengraphDescription
          opengraphImage {
            sourceUrl
          }
          twitterTitle
          twitterDescription
          twitterImage {
            sourceUrl
          }
          schema {
            raw
          }
        }
      }
    }
  `,
  organizations: gql`
    query GetOrganizationBySlug($slug: ID!) {
      organization(id: $slug, idType: SLUG) {
        id
        title
        content
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        author {
          node {
            name
            avatarUrl
          }
        }
        seo {
          title
          metaDesc
          canonical
          opengraphTitle
          opengraphDescription
          opengraphImage {
            sourceUrl
          }
          twitterTitle
          twitterDescription
          twitterImage {
            sourceUrl
          }
          schema {
            raw
          }
        }
      }
    }
  `,
  resources: gql`
    query GetResourceBySlug($slug: ID!) {
      resource(id: $slug, idType: SLUG) {
        id
        title
        content
        excerpt
        slug
        date
        featuredImage {
          node {
            sourceUrl
            altText
          }
        }
        author {
          node {
            name
            avatarUrl
          }
        }
        seo {
          title
          metaDesc
          canonical
          opengraphTitle
          opengraphDescription
          opengraphImage {
            sourceUrl
          }
          twitterTitle
          twitterDescription
          twitterImage {
            sourceUrl
          }
          schema {
            raw
          }
        }
      }
    }
  `,
  faqs: gql`
    query GetFaqBySlug($slug: ID!) {
      faq(id: $slug, idType: SLUG) {
        id
        title
        content
        slug
        date
        author {
          node {
            name
            avatarUrl
          }
        }
        seo {
          title
          metaDesc
          canonical
          opengraphTitle
          opengraphDescription
          opengraphImage {
            sourceUrl
          }
          twitterTitle
          twitterDescription
          twitterImage {
            sourceUrl
          }
          schema {
            raw
          }
        }
      }
    }
  `,
};

// Translations
type Locale = 'el' | 'en';

type Translations = {
  [key in Locale]: {
    eyebrow: string;
    publishedOn: string;
    by: string;
    backToArticles: string;
    readTime: string;
    relatedArticles: string;
    exploreMore: string;
    loading: string;
    error: string;
    notFound: string;
    categories: string;
    tags: string;
    shareArticle: string;
    tableOfContents: string;
  };
};

const translations: Translations = {
  el: {
    eyebrow: 'Άρθρο',
    publishedOn: 'Δημοσιεύθηκε',
    by: 'από',
    backToArticles: 'Επιστροφή στα Άρθρα',
    readTime: 'λεπτά χρόνος ανάγνωσης',
    relatedArticles: 'Σχετικά Άρθρα',
    exploreMore: 'Ανακαλύψτε Περισσότερα',
    loading: 'Φόρτωση άρθρου...',
    error: 'Σφάλμα κατά τη φόρτωση του άρθρου',
    notFound: 'Το άρθρο δεν βρέθηκε',
    categories: 'Κατηγορίες',
    tags: 'Ετικέτες',
    shareArticle: 'Κοινοποίηση άρθρου',
    tableOfContents: 'Περιεχόμενα',
  },
  en: {
    eyebrow: 'Article',
    publishedOn: 'Published on',
    by: 'by',
    backToArticles: 'Back to Articles',
    readTime: 'min read',
    relatedArticles: 'Related Articles',
    exploreMore: 'Explore More',
    loading: 'Loading article...',
    error: 'Error loading article',
    notFound: 'Article not found',
    categories: 'Categories',
    tags: 'Tags',
    shareArticle: 'Share Article',
    tableOfContents: 'Table of Contents',
  },
};

function calculateReadTime(content: string): number {
  const wordsPerMinute = 200;
  const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
}

interface Heading {
  id: string;
  text: string;
  level: number;
}

/**
 * Extract H2 headings from HTML content for table of contents
 */
function extractHeadings(html: string): Heading[] {
  const headings: Heading[] = [];
  const headingRegex = /<h2[^>]*>(.*?)<\/h2>/gi;
  let match;

  while ((match = headingRegex.exec(html)) !== null) {
    // Strip HTML tags from heading text
    const text = match[1].replace(/<[^>]+>/g, '').trim();
    // Create URL-friendly ID from heading text
    const id = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);
    
    headings.push({ id, text, level: 2 });
  }

  return headings;
}

/**
 * Add IDs to H2 headings in HTML content for anchor links
 */
function addHeadingIds(html: string): string {
  let headingIndex = 0;
  
  return html.replace(/<h2([^>]*)>(.*?)<\/h2>/gi, (match, attrs, content) => {
    const text = content.replace(/<[^>]+>/g, '').trim();
    const id = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);
    
    // Check if ID already exists in attrs
    if (attrs.includes('id=')) {
      return match;
    }
    
    headingIndex++;
    return `<h2${attrs} id="${id}-${headingIndex}">${content}</h2>`;
  });
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildContentWithCtas(
  html: string,
  options: { showChecklistCta: boolean }
) {
  if (!options.showChecklistCta) {
    return [
      <div key="content-root" dangerouslySetInnerHTML={{ __html: html }} />
    ];
  }

  const introSentence =
    'Σε αυτό το άρθρο θα δεις, με απλά λόγια, τι πρέπει να έχει μια επαγγελματική ιστοσελίδα πριν θεωρηθεί πραγματικά έτοιμη.';
  const introMarker = '<!--CHECKLIST_CTA_INTRO-->';
  const faqMarker = '<!--CHECKLIST_CTA_FAQ-->';

  let enhancedHtml = html;

  const introRegex = new RegExp(
    `(<p[^>]*>[\\s\\S]*?${escapeRegExp(introSentence)}[\\s\\S]*?<\\/p>)`,
    'i'
  );

  if (introRegex.test(enhancedHtml)) {
    enhancedHtml = enhancedHtml.replace(introRegex, `$1${introMarker}`);
  }

  const lastDetailsIndex = enhancedHtml.lastIndexOf('</details>');
  if (lastDetailsIndex !== -1) {
    const insertAt = lastDetailsIndex + '</details>'.length;
    enhancedHtml =
      enhancedHtml.slice(0, insertAt) +
      faqMarker +
      enhancedHtml.slice(insertAt);
  }

  const segments = enhancedHtml.split(
    /(<!--CHECKLIST_CTA_INTRO-->|<!--CHECKLIST_CTA_FAQ-->)/g
  );
  const nodes: ReactNode[] = [];

  segments.forEach((segment, index) => {
    if (!segment) return;
    if (segment === introMarker || segment === faqMarker) {
      nodes.push(<ChecklistCTA key={`checklist-cta-${index}`} />);
      return;
    }
    nodes.push(
      <div
        key={`content-${index}`}
        dangerouslySetInnerHTML={{ __html: segment }}
      />
    );
  });

  return nodes;
}

/**
 * Transform plain text FAQ patterns into accordion format.
 * Detects patterns like:
 * - <p><strong>Question?</strong></p><p>Answer.</p>
 * - <p><b>Question?</b></p><p>Answer.</p>
 * Allows attributes and inline tags inside the question/answer,
 * and only converts if the question ends with ? or ; (Greek question mark).
 */
function transformFAQContent(html: string): string {
  const qaPattern =
    /<p[^>]*>\s*<(strong|b)[^>]*>([\s\S]*?)<\/\1>\s*<\/p>\s*<p[^>]*>([\s\S]*?)<\/p>/gi;

  html = html.replace(qaPattern, (match, _tag, questionHtml, answerHtml) => {
    const questionText = questionHtml.replace(/<[^>]+>/g, '').trim();
    if (!/[?;]$/.test(questionText)) {
      return match;
    }
    return `<details><summary>${questionHtml}</summary><div class="faq-answer"><p>${answerHtml}</p></div></details>`;
  });

  return html;
}

export default function PostDetail({ postType = 'post' }: PostDetailProps) {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const localizedPath = useLocalizedPath();
  const locale = (language ?? 'el') as Locale;
  const t = translations[locale];

  // Archive path mapping
  const archivePathMap: Record<string, string> = {
    post: '/articles',
    knowledge: '/knowledge',
    guides: '/guides',
    tools: '/tools',
    legislation: '/legislation',
    organizations: '/organizations',
    resources: '/resources',
    faqs: '/faqs',
  };
  const archivePath = archivePathMap[postType] || '/articles';

  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPost() {
      if (!slug) {
        setError(t.notFound);
        setLoading(false);
        return;
      }

      try {
        const client = new GraphQLClient(WP_GRAPHQL_URL);
        
        // Select the appropriate query based on postType
        const query = postType === 'post' ? POST_BY_SLUG_QUERY : (CPT_QUERIES[postType] || POST_BY_SLUG_QUERY);
        const data = await client.request<{ post?: Post; [key: string]: unknown }>(query, { slug });

        // Extract the post from the response based on postType
        let postData: Post | undefined;
        
        if (postType === 'post') {
          postData = data.post;
        } else {
          // For CPTs, the key is singular form (e.g., knowledgeItem, guide, etc.)
          const singularMap: Record<string, string> = {
            knowledge: 'knowledgeItem',
            guides: 'guide',
            tools: 'tool',
            legislation: 'legislationItem',
            organizations: 'organization',
            resources: 'resource',
            faqs: 'faq',
          };
          const key = singularMap[postType] || postType;
          postData = data[key] as Post | undefined;
        }

        if (postData) {
          const fallbackAvatarUrl = postData.author?.node?.avatar?.url;
          const authorNode = postData.author?.node;
          const normalizedAuthor = authorNode
            ? {
                node: {
                  ...authorNode,
                  avatarUrl: authorNode.avatarUrl ?? fallbackAvatarUrl,
                },
              }
            : undefined;

          setPost({
            ...postData,
            author: normalizedAuthor,
          });
        } else {
          setError(t.notFound);
        }
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(t.error);
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [slug, t, postType]);

  if (loading) {
    return (
      <div className="relative min-h-screen bg-[#0b0a0e]">
        <PixelBackground />
        <Header variant="simple" />
        <main className="flex min-h-[60vh] items-center justify-center relative z-10">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white/30" />
            <p className="mt-4 text-white/70">{t.loading}</p>
          </div>
        </main>
        <Footer variant="compact" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="relative min-h-screen bg-[#0b0a0e]">
        <PixelBackground />
        <Header variant="simple" />
        <main className="flex min-h-[60vh] items-center justify-center relative z-10">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">404</h1>
            <p className="text-white/70">{error || t.notFound}</p>
            <Button
              as="a"
              href={localizedPath(archivePath)}
              variant="primary"
              size="lg"
              className="mt-6"
            >
              {t.backToArticles}
            </Button>
          </div>
        </main>
        <Footer variant="compact" />
      </div>
    );
  }

  // Format date
  const formattedDate = new Date(post.date).toLocaleDateString(
    locale === 'el' ? 'el-GR' : 'en-US',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }
  );

  const readTime = calculateReadTime(post.content);

  // Extract headings for table of contents
  const headings = extractHeadings(post.content);
  const processedContent = addHeadingIds(transformFAQContent(post.content));
  const showChecklistCta = slug === 'checklist-epaggelmatikis-istoselidas';
  const contentNodes = buildContentWithCtas(processedContent, { showChecklistCta });

  // Generate SEO metadata
  const canonicalPath = withLanguagePrefix(`${archivePath}/${post.slug}`, locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(`${archivePath}/${post.slug}`, locale);

  return (
    <>
      <Seo
        title={post.title}
        description={post.excerpt?.replace(/<[^>]*>/g, '').slice(0, 160) || ''}
        url={canonicalUrl}
        image={post.featuredImage?.node?.sourceUrl}
        hreflang={hreflang}
        yoast={post.seo}
      />
      
      {/* Yoast SEO Schema */}
      {post.seo?.schema?.raw && (
        <JsonLd
          json={JSON.parse(post.seo.schema.raw)}
        />
      )}

      <div className="relative min-h-screen bg-[#0b0a0e] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10">
          {/* Hero Section */}
          <HeroSection className="relative overflow-hidden pt-32 pb-12 md:pt-36 lg:pt-40 lg:pb-16">
            <div className="absolute inset-0 geometric-pattern pointer-events-none" aria-hidden="true" />
            <div
              className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black pointer-events-none"
              aria-hidden="true"
            />

            <Container className="relative">
              {/* Back Link */}
              <Link
                to={localizedPath(archivePath)}
                className="inline-flex items-center text-sm text-white/60 hover:text-white transition-colors mb-8 group"
              >
                <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
                {t.backToArticles}
              </Link>

              {/* Eyebrow */}
              <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70 mb-4">
                {t.eyebrow}
              </p>

              {/* Title */}
              <Display
                variant="h1"
                as="h1"
                className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight mb-6"
              >
                {post.title}
              </Display>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-white/60">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-brand-highlight/70" />
                  <span>{formattedDate}</span>
                </div>

                {post.author?.node && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-brand-highlight/70" />
                    <span>{t.by} {post.author.node.name}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-brand-highlight/70" />
                  <span>{readTime} {t.readTime}</span>
                </div>
              </div>
            </Container>
          </HeroSection>

          {/* Featured Image */}
          {post.featuredImage?.node && (
            <section className="py-8 lg:py-12">
              <Container>
                <div className="relative aspect-video rounded-2xl overflow-hidden border border-white/15 bg-black/70 shadow-glass">
                  <img
                    src={post.featuredImage.node.sourceUrl}
                    alt={post.featuredImage.node.altText || post.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </Container>
            </section>
          )}

          {/* Content Section */}
          <section className="py-12 lg:py-16">
            <Container>
              <div className="grid gap-12 lg:grid-cols-[1fr_280px] lg:gap-16 items-start">
                {/* Main Content */}
                <article className="min-w-0">
                  <div className="articleContent select-text">
                    {contentNodes}
                  </div>

                  {/* Tags */}
                  {post.tags?.nodes && post.tags.nodes.length > 0 && (
                    <div className="mt-12 pt-8 border-t border-white/10">
                      <p className="text-sm font-medium text-white/60 mb-4">{t.tags}</p>
                      <div className="flex flex-wrap gap-2">
                        {post.tags.nodes.map((tag) => (
                          <span
                            key={tag.slug}
                            className="px-3 py-1 text-xs text-white/70 bg-white/5 rounded-full border border-white/10 hover:bg-white/10 transition-colors"
                          >
                            #{tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </article>

                {/* Sidebar */}
                <aside className="hidden lg:block space-y-6 lg:sticky lg:top-28 lg:self-start">
                  {/* Table of Contents */}
                  {headings.length > 0 && (
                    <div className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 shadow-glass">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-highlight/80 mb-4">
                        {t.tableOfContents}
                      </p>
                      <nav className="space-y-2 max-h-[300px] overflow-y-auto pr-2 toc-scrollbar">
                        {headings.map((heading, index) => (
                          <a
                            key={`${heading.id}-${index}`}
                            href={`#${heading.id}-${index + 1}`}
                            className="block text-sm text-white/70 hover:text-white transition-colors py-1 border-l-2 border-white/10 hover:border-brand-highlight/70 pl-3"
                          >
                            {heading.text}
                          </a>
                        ))}
                      </nav>
                    </div>
                  )}

                  {/* Share */}
                  <div className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 shadow-glass">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-highlight/80 mb-4">
                      {t.shareArticle}
                    </p>
                    <div className="flex gap-3">
                      <a
                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalUrl)}&text=${encodeURIComponent(post.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-colors hover:bg-white/10 hover:text-white hover:border-white/25"
                        aria-label="Share on Twitter"
                      >
                        <Twitter className="h-5 w-5" />
                      </a>
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-colors hover:bg-white/10 hover:text-white hover:border-white/25"
                        aria-label="Share on LinkedIn"
                      >
                        <Linkedin className="h-5 w-5" />
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-colors hover:bg-white/10 hover:text-white hover:border-white/25"
                        aria-label="Share on Facebook"
                      >
                        <Facebook className="h-5 w-5" />
                      </a>
                      <a
                        href={`https://reddit.com/submit?url=${encodeURIComponent(canonicalUrl)}&title=${encodeURIComponent(post.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-colors hover:bg-white/10 hover:text-white hover:border-white/25"
                        aria-label="Share on Reddit"
                      >
                        <RedditIcon />
                      </a>
                      <button
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition-colors hover:bg-white/10 hover:text-white hover:border-white/25"
                        aria-label="Copy link"
                        onClick={() => navigator.clipboard.writeText(canonicalUrl)}
                      >
                        <Share2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Author Card */}
                  {post.author?.node && (
                    <div className="rounded-2xl border border-white/15 bg-black/70 backdrop-blur-xl p-6 shadow-glass">
                      {post.author.node.avatarUrl && (
                        <img
                          src={post.author.node.avatarUrl}
                          alt={post.author.node.name}
                          className="w-16 h-16 rounded-full border-2 border-brand-highlight/30 mb-4"
                        />
                      )}
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-highlight/80 mb-2">
                        {t.by}
                      </p>
                      <p className="text-lg font-semibold text-white">{post.author.node.name}</p>
                    </div>
                  )}
                </aside>
              </div>
            </Container>
          </section>

          {/* Related Articles CTA */}
          <section className="pt-0 pb-24">
            <Container>
              <div className="relative overflow-hidden rounded-[32px] border border-white/15 bg-black/70 p-8 md:p-12 shadow-glass">
                <div className="pointer-events-none absolute -right-24 -top-32 h-72 w-72 rounded-full bg-brand-highlight/20 blur-3xl opacity-60" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-white/10 blur-3xl opacity-60" />

                <div className="relative text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80 mb-4">
                    {t.relatedArticles}
                  </p>

                  <Display
                    variant="h1"
                    as="h2"
                    className="mx-auto max-w-3xl text-2xl font-semibold text-white md:text-4xl mb-6"
                  >
                    {locale === 'el'
                      ? 'Ανακαλύψτε Περισσότερα Άρθρα'
                      : 'Discover More Articles'}
                  </Display>

                  <p className="mx-auto max-w-2xl text-lg text-white/70 mb-8">
                    {locale === 'el'
                      ? 'Εξερευνήστε τη συλλογή άρθρων μας για περισσότερες πληροφορίες και ιδέες.'
                      : 'Explore our collection of articles for more insights and ideas.'}
                  </p>

                  <Button
                    as="a"
                    href={localizedPath(archivePath)}
                    variant="primary"
                    size="lg"
                  >
                    {t.exploreMore} →
                  </Button>
                </div>
              </div>
            </Container>
          </section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
